import { useEffect, useRef, useState, useCallback } from 'react';
import { useSupabaseThoughts } from '../../hooks/useSupabaseThoughts';
import getRandomPastelColor from '../../utils/getRandomPastelColor';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import ThoughtModal from './ThoughtModal';
import SelectedThoughtModal from './SelectedThoughtModal';
import ThoughtNote from './ThoughtNote';
import Seo from "../Seo";

const WORLD = 5000;          // size of the pannable canvas
const MIN_Z = 0.6;           // zoom caps
const MAX_Z = 2.4;
const Z_STEP = 0.2;
const WRAP_BUFFER = 60;      // px of margin kept before looping around
const EASE = (t) => 1 - Math.pow(1 - t, 3);

export default function Headspace() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const isGuest = sessionStorage.getItem('guest') === 'true';
    const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const {
        thoughts, setThoughts, fetchThoughts,
        updateThoughtPosition, deleteThought, addThought,
    } = useSupabaseThoughts();

    const [newThought, setNewThought] = useState('');
    const [thoughtPosition, setThoughtPosition] = useState(null);
    const [showThoughtModal, setShowThoughtModal] = useState(false);
    const [adminThoughtMode, setAdminThoughtMode] = useState(false);
    const [showCenterZone, setShowCenterZone] = useState(true);
    const [selectedThought, setSelectedThought] = useState(null);
    const [thoughtBeingEdited, setThoughtBeingEdited] = useState(null);

    // refs — transform lives outside React state so panning never re-renders notes
    const viewportRef = useRef(null);
    const worldRef = useRef(null);
    const view = useRef({ tx: 0, ty: 0, z: 1 });     // current translate + zoom
    const vel = useRef({ x: 0, y: 0 });              // momentum velocity
    const pan = useRef({ active: false, moved: 0, lastX: 0, lastY: 0, blank: false });
    const rafPan = useRef(null);
    const rafZoom = useRef(null);

    // ---------- auth gating (unchanged) ----------
    useEffect(() => {
        if (!loading && !user && !isGuest) navigate('/login');
    }, [user, loading, navigate, isGuest]);

    useEffect(() => {
        if (user?.email === 'ethan.younghoon.park@gmail.com') setAdminThoughtMode(true);
    }, [user]);

    // ---------- transform helpers ----------
    const applyTransform = useCallback(() => {
        const { tx, ty, z } = view.current;
        if (worldRef.current) {
            worldRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${z})`;
        }
    }, []);

    const clampView = useCallback(() => {
        const v = view.current;
        const vw = window.innerWidth, vh = window.innerHeight;
        v.tx = Math.min(0, Math.max(Math.min(0, vw - WORLD * v.z), v.tx));
        v.ty = Math.min(0, Math.max(Math.min(0, vh - WORLD * v.z), v.ty));
    }, []);

    // Wrap the view so panning off one edge loops back in from the other.
    // The jump happens inside the empty margin, so it reads as endless.
    const wrapView = useCallback(() => {
        const v = view.current;
        const vw = window.innerWidth, vh = window.innerHeight;
        const maxSX = WORLD * v.z - vw;   // scrollable span horizontally
        const maxSY = WORLD * v.z - vh;

        if (maxSX > WRAP_BUFFER * 2) {
            if (v.tx > -WRAP_BUFFER) v.tx = -maxSX + WRAP_BUFFER;
            else if (v.tx < -maxSX + WRAP_BUFFER) v.tx = -WRAP_BUFFER;
        } else {
            v.tx = Math.min(0, Math.max(Math.min(0, -maxSX), v.tx)); // too small to wrap → clamp
        }

        if (maxSY > WRAP_BUFFER * 2) {
            if (v.ty > -WRAP_BUFFER) v.ty = -maxSY + WRAP_BUFFER;
            else if (v.ty < -maxSY + WRAP_BUFFER) v.ty = -WRAP_BUFFER;
        } else {
            v.ty = Math.min(0, Math.max(Math.min(0, -maxSY), v.ty));
        }
    }, []);

    const commit = useCallback(() => {          // hard bounds — zoom & recenter
        clampView();
        applyTransform();
    }, [clampView, applyTransform]);

    const commitWrap = useCallback(() => {      // looping — interactive panning
        wrapView();
        applyTransform();
    }, [wrapView, applyTransform]);

    const animateTo = useCallback((gx, gy, animated) => {
        cancelAnimationFrame(rafPan.current);
        const v = view.current;
        if (!animated || reduce) { v.tx = gx; v.ty = gy; commit(); return; }
        const sx = v.tx, sy = v.ty, t0 = performance.now(), dur = 600;
        const step = (now) => {
            const p = Math.min(1, (now - t0) / dur), e = EASE(p);
            v.tx = sx + (gx - sx) * e;
            v.ty = sy + (gy - sy) * e;
            commit();
            if (p < 1) rafPan.current = requestAnimationFrame(step);
        };
        rafPan.current = requestAnimationFrame(step);
    }, [commit, reduce]);

    const centerView = useCallback((animated) => {
        const z = view.current.z;
        animateTo(
            window.innerWidth / 2 - z * (WORLD / 2),
            window.innerHeight / 2 - z * (WORLD / 2),
            animated,
        );
    }, [animateTo]);

    // zoom toward a screen point (cx, cy)
    const zoomAt = useCallback((nz, cx, cy, animated) => {
        cancelAnimationFrame(rafZoom.current);
        const v = view.current;
        nz = Math.min(MAX_Z, Math.max(MIN_Z, nz));
        if (Math.abs(nz - v.z) < 1e-4) return;
        const wcx = (cx - v.tx) / v.z, wcy = (cy - v.ty) / v.z;
        const settle = (zz) => {
            v.z = zz;
            v.tx = cx - zz * wcx;
            v.ty = cy - zz * wcy;
            commit();
        };
        if (!animated || reduce) { settle(nz); return; }
        const z0 = v.z, t0 = performance.now(), dur = 190;
        const step = (now) => {
            const p = Math.min(1, (now - t0) / dur), e = EASE(p);
            settle(z0 + (nz - z0) * e);
            if (p < 1) rafZoom.current = requestAnimationFrame(step);
        };
        rafZoom.current = requestAnimationFrame(step);
    }, [commit, reduce]);

    const startMomentum = useCallback(() => {
        cancelAnimationFrame(rafPan.current);
        const loop = () => {
            if (pan.current.active) return;
            const vv = vel.current, v = view.current;
            vv.x *= 0.93; vv.y *= 0.93;
            if (Math.abs(vv.x) < 0.2 && Math.abs(vv.y) < 0.2) return;
            v.tx += vv.x; v.ty += vv.y;
            wrapView();                 // loop around rather than hit a wall
            applyTransform();
            rafPan.current = requestAnimationFrame(loop);
        };
        rafPan.current = requestAnimationFrame(loop);
    }, [wrapView, applyTransform]);

    // ---------- init: center, fetch, wheel + resize listeners ----------
    useEffect(() => {
        const v = view.current;
        v.tx = window.innerWidth / 2 - WORLD / 2;
        v.ty = window.innerHeight / 2 - WORLD / 2;
        commit();
        fetchThoughts();

        const onResize = () => commit();
        window.addEventListener('resize', onResize);

        const vp = viewportRef.current;
        const onWheel = (e) => {
            e.preventDefault();
            if (e.ctrlKey || e.metaKey) {
                zoomAt(view.current.z * (1 - e.deltaY * 0.01), e.clientX, e.clientY, false);
            } else {
                cancelAnimationFrame(rafPan.current);
                view.current.tx -= e.deltaX;
                view.current.ty -= e.deltaY;
                commitWrap();
            }
        };
        vp.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            window.removeEventListener('resize', onResize);
            vp.removeEventListener('wheel', onWheel);
            cancelAnimationFrame(rafPan.current);
            cancelAnimationFrame(rafZoom.current);
        };
    }, [commit, commitWrap, zoomAt, fetchThoughts]);

    // ---------- periodic refresh (unchanged) ----------
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fetchThoughts();
        }, 60000);
        return () => clearInterval(id);
    }, [fetchThoughts]);

    // ---------- intro veil fade (unchanged) ----------
    useEffect(() => {
        if (user || isGuest) {
            const t = setTimeout(() => setShowCenterZone(false), 2250);
            return () => clearTimeout(t);
        }
    }, [user, isGuest]);

    // ---------- viewport pan (pointer) ----------
    const onPointerDown = (e) => {
        if (
            e.target.closest('.thought-note') ||
            e.target.closest('button') ||
            e.target.closest('a') ||
            e.target.closest('input') ||
            e.target.closest('textarea')
        ) { pan.current.blank = false; return; }

        cancelAnimationFrame(rafPan.current);
        pan.current = { active: true, moved: 0, lastX: e.clientX, lastY: e.clientY, blank: true };
        vel.current = { x: 0, y: 0 };
        viewportRef.current.setPointerCapture(e.pointerId);
        viewportRef.current.style.cursor = 'grabbing';
    };

    const onPointerMove = (e) => {
        if (!pan.current.active) return;
        const dx = e.clientX - pan.current.lastX;
        const dy = e.clientY - pan.current.lastY;
        view.current.tx += dx;
        view.current.ty += dy;
        vel.current = { x: dx, y: dy };
        pan.current.moved += Math.abs(dx) + Math.abs(dy);
        pan.current.lastX = e.clientX;
        pan.current.lastY = e.clientY;
        commitWrap();
    };

    const onPointerUp = (e) => {
        if (!pan.current.active) return;
        pan.current.active = false;
        if (viewportRef.current) viewportRef.current.style.cursor = 'grab';
        const { blank, moved } = pan.current;
        if (!reduce) startMomentum();

        // a blank tap that didn't drag → drop a new thought here
        if (blank && moved < 6 && !isGuest) {
            const v = view.current;
            setThoughtPosition({ x: (e.clientX - v.tx) / v.z, y: (e.clientY - v.ty) / v.z });
            setThoughtBeingEdited(null);
            setNewThought('');
            setShowThoughtModal(true);
        }
    };

    // ---------- dragging an individual note ----------
    const handleNoteDrag = (e, t) => {
        if (isGuest) return;
        e.stopPropagation();
        const sx = e.clientX, sy = e.clientY, ox = t.x, oy = t.y;
        let dragged = false, nx = ox, ny = oy;

        const move = (me) => {
            const z = view.current.z;
            nx = ox + (me.clientX - sx) / z;
            ny = oy + (me.clientY - sy) / z;
            dragged = true;
            setThoughts((prev) => prev.map((tt) => (tt.id === t.id ? { ...tt, x: nx, y: ny } : tt)));
        };
        const up = async () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            if (dragged) await updateThoughtPosition(t.id, nx, ny);
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
    };

    if (loading) return null;

    return (
        <>
            <Seo title="Headspace" path="/headspace" noindex />

            <div
                ref={viewportRef}
                className="fixed inset-0 overflow-hidden bg-[#fdfaf6] cursor-grab"
                style={{ touchAction: 'none', fontFamily: 'Crimson Text, serif' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => { pan.current.active = false; }}
            >
                <div
                    ref={worldRef}
                    style={{
                        width: WORLD, height: WORLD, position: 'relative',
                        transformOrigin: '0 0', willChange: 'transform',
                    }}
                >
                    {thoughts.map((t) => (
                        <ThoughtNote
                            key={t.id}
                            thought={t}
                            userId={user?.id}
                            onDrag={isGuest ? undefined : handleNoteDrag}
                            onClick={(_e, thought) => setSelectedThought(thought)}
                        />
                    ))}
                </div>
            </div>

            {/* intro veil */}
            <div
                className={`fixed inset-0 z-40 flex flex-col items-center justify-center bg-[#fdfaf6] transition-opacity duration-1000 ${
                    showCenterZone ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                style={{ fontFamily: 'Crimson Text, serif' }}
            >
                <h1 className="text-5xl font-bold mb-4">Headspace</h1>
                <p className="text-lg mb-6">Click anywhere to leave a thought.</p>
            </div>

            {/* controls: recenter + zoom */}
            <div className="fixed top-6 right-6 flex flex-col gap-2 z-50">
                <button
                    onClick={() => centerView(true)}
                    className="bg-white px-3 py-1 rounded shadow hover:text-[#b483d1] text-sm"
                    title="Recenter"
                >
                    Recenter
                </button>
                <button
                    onClick={() => zoomAt(view.current.z + Z_STEP, window.innerWidth / 2, window.innerHeight / 2, true)}
                    className="bg-white w-9 h-9 rounded shadow hover:text-[#b483d1] text-xl leading-none"
                    aria-label="Zoom in"
                >
                    +
                </button>
                <button
                    onClick={() => zoomAt(view.current.z - Z_STEP, window.innerWidth / 2, window.innerHeight / 2, true)}
                    className="bg-white w-9 h-9 rounded shadow hover:text-[#b483d1] text-xl leading-none"
                    aria-label="Zoom out"
                >
                    −
                </button>
            </div>

            <button
                onClick={() => window.history.back()}
                className="fixed top-6 left-6 bg-white text-accent px-3 py-1 rounded shadow hover:text-[#b483d1] z-50"
            >
                ← Back
            </button>

            <ThoughtModal
                visible={showThoughtModal}
                thoughtContent={newThought}
                onChange={setNewThought}
                onSave={async () => {
                    const wordCount = newThought.trim().split(/\s+/).filter(Boolean).length;
                    if (wordCount < 1) { alert('Try writing something first.'); return; }
                    if (wordCount > 30) { alert('Please limit to 30 words.'); return; }
                    if (thoughtBeingEdited) await deleteThought(thoughtBeingEdited.id);
                    await addThought({
                        content: newThought.trim(),
                        x: thoughtPosition.x,
                        y: thoughtPosition.y,
                        color: thoughtBeingEdited ? thoughtBeingEdited.color : getRandomPastelColor(),
                        user_id: user?.id,
                        display_name: user?.user_metadata?.display_name,
                    });
                    setShowThoughtModal(false);
                    setNewThought('');
                    setThoughtBeingEdited(null);
                }}
                onCancel={() => { setShowThoughtModal(false); setThoughtBeingEdited(null); }}
                isEditing={!!thoughtBeingEdited}
            />

            <SelectedThoughtModal
                thought={selectedThought}
                onClose={() => setSelectedThought(null)}
                onEdit={isGuest ? null : () => {
                    setNewThought(selectedThought.content);
                    setThoughtPosition({ x: selectedThought.x, y: selectedThought.y });
                    setSelectedThought(null);
                    setShowThoughtModal(true);
                    setThoughtBeingEdited(selectedThought);
                }}
                onDelete={isGuest ? null : async () => {
                    await deleteThought(selectedThought.id);
                    setSelectedThought(null);
                }}
                adminMode={isGuest ? false : adminThoughtMode}
                currentUserId={isGuest ? null : user?.id}
            />
        </>
    );
}